import { useMutation } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import { ReactNode, SyntheticEvent, useCallback, useMemo } from 'react';

import { LogoutDocument } from '@willow/graphql-iso/src/portal';
import { PORTAL_LOGIN_PATH } from '@willow/types-iso';

import { hardNavigate } from './hardNavigate';
import { signOut } from './portalAuthApi';
import { PortalAuthContext, PortalAuthContextValue, PortalAuthMode } from './portalAuthContext';

// useAuth0 only works inside an Auth0Provider, so the Auth0 half lives in its own component
// that cookie mode never renders.
const Auth0LogoutBridge = ({ children }: { children: ReactNode }) => {
  const [gqlLogout] = useMutation(LogoutDocument);
  const { logout: auth0Logout } = useAuth0();

  const logout = useCallback(
    async (event?: SyntheticEvent) => {
      event?.preventDefault();
      try {
        await gqlLogout();
      } catch {
        // eslint-disable-next-line no-console -- log for debugging
        console.error('Error expiring token');
      }
      await auth0Logout({ logoutParams: { returnTo: window.location.origin } });
    },
    [gqlLogout, auth0Logout],
  );

  const value = useMemo<PortalAuthContextValue>(() => ({ mode: 'auth0', logout }), [logout]);
  return <PortalAuthContext.Provider value={value}>{children}</PortalAuthContext.Provider>;
};

const CookieSessionLogoutProvider = ({ children }: { children: ReactNode }) => {
  const logout = useCallback(async (event?: SyntheticEvent) => {
    // The link this sits on would otherwise unload the page and abort the sign-out below.
    event?.preventDefault();
    // The result does not change what happens next: the cookie is host-only, and the login
    // page is where a stale one gets replaced.
    await signOut();
    hardNavigate(PORTAL_LOGIN_PATH);
  }, []);

  const value = useMemo<PortalAuthContextValue>(() => ({ mode: 'cookie', logout }), [logout]);
  return <PortalAuthContext.Provider value={value}>{children}</PortalAuthContext.Provider>;
};

export const PortalAuthProvider = ({ mode, children }: { mode: PortalAuthMode; children: ReactNode }) =>
  mode === 'cookie' ? (
    <CookieSessionLogoutProvider>{children}</CookieSessionLogoutProvider>
  ) : (
    <Auth0LogoutBridge>{children}</Auth0LogoutBridge>
  );
